import Phaser from '../../lib/phaser.js';
import { HealthBar } from '../../common/health-bar.js';
import { BATTLE_ASSET_KEYS } from '../../assets/asset-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../../assets/font-keys.js';
import { DataUtils } from '../../utils/data-utils.js';

export class BattleMonster {
  /** @protected @type {Phaser.Scene} */
  _scene;
  /** @protected @type {import('../../types/typedef.js').Monster} */
  _monsterDetails;
  /** @protected @type {HealthBar} */
  _healthBar;
  /** @protected @type {Phaser.GameObjects.Image} */
  _phaserGameObject;
  /** @protected @type {number} */
  _currentHealth;
  /** @protected @type {number} */
  _maxHealth;
  /** @protected @type {import('../../types/typedef.js').Attack[]} */
  _monsterAttacks;
  /** @protected @type {Phaser.GameObjects.Container} */
  _phaserHealthBarGameContainer;
  /** @protected @type {boolean} */
  _skipBattleAnimations;
  /** @protected @type {Phaser.GameObjects.Text} */
  _monsterHealthBarLevelText;
  /** @protected @type {Phaser.GameObjects.Text} */
  _monsterNameText;

  /**
   * @param {import('../../types/typedef.js').BattleMonsterConfig} config
   * @param {import('../../types/typedef.js').Coordinate} position
   */
  constructor(config, position) {
    if (this.constructor === BattleMonster) {
      throw new Error('BattleMonster is an abstract class and cannot be instantiated.');
    }
    this._scene = config.scene;
    this._monsterDetails = config.monsterDetails;
    this._currentHealth = this._monsterDetails.currentHp;
    this._maxHealth = this._monsterDetails.maxHp;
    this._monsterAttacks = [];
    this._skipBattleAnimations = config.skipBattleAnimations || false;

    this._phaserGameObject = this._scene.add
      .image(position.x, position.y, this._monsterDetails.assetKey, this._monsterDetails.assetFrame || 0)
      .setAlpha(0);
    // Small Catchimon battlers (80x80) carry an `assetScale` so they read at the same magnitude
    // as the 256px legacy sprites. Legacy sprites have assetScale 1 (or undefined → 1).
    if (this._monsterDetails.assetScale && this._monsterDetails.assetScale !== 1) {
      this._phaserGameObject.setScale(this._monsterDetails.assetScale);
    }
    this.#createHealthBarComponents(config.scaleHealthBarBackgroundImageByY);
    this._healthBar.setMeterPercentageAnimated(this._currentHealth / this._maxHealth, {
      skipBattleAnimations: true,
    });

    this.#loadAttacks();
  }

  /**
   * Resolve the attack ids to load onto this monster, healing stale/partial saves. Old (and some
   * captured) monsters carry an `attackIds` list from before the 4-move-per-species update, so they
   * would battle with only 1 move. If the instance has fewer attack ids than its SPECIES defines
   * (every species has 4 in monsters.json), we re-derive the full moveset from the species. The
   * guard (`?? []`) also means an undefined list never throws and blacks out the battle.
   * @returns {number[]}
   */
  #resolveAttackIds() {
    const instanceAttackIds = this._monsterDetails.attackIds ?? [];
    const species = DataUtils.getMonsterById(this._scene, this._monsterDetails.monsterId);
    const speciesAttackIds = species?.attackIds ?? [];
    if (speciesAttackIds.length > instanceAttackIds.length) {
      return speciesAttackIds;
    }
    return instanceAttackIds;
  }

  /**
   * (Re)build `_monsterAttacks` from the resolved (full) attack id list for the current monster.
   * @returns {void}
   */
  #loadAttacks() {
    this._monsterAttacks = [];
    this.#resolveAttackIds().forEach((attackId) => {
      const monsterAttack = DataUtils.getMonsterAttack(this._scene, attackId);
      if (monsterAttack !== undefined) {
        this._monsterAttacks.push(monsterAttack);
      }
    });
  }

  /** @type {number} */
  get currentHp() {
    return this._currentHealth;
  }

  /** @type {number} */
  get maxHp() {
    return this._maxHealth;
  }

  /** @type {boolean} */
  get isFainted() {
    return this._currentHealth <= 0;
  }

  /** @type {string | undefined} element (flame/frost/…) — drives the per-element strike tint. */
  get element() {
    return this._monsterDetails?.element;
  }

  /** @type {string} */
  get name() {
    return this._monsterDetails.name;
  }

  /** @type {import('../../types/typedef.js').Attack[]} */
  get attacks() {
    return [...this._monsterAttacks];
  }

  /** @type {number} */
  get baseAttack() {
    return this._monsterDetails.currentAttack;
  }

  /** @type {number} */
  get level() {
    return this._monsterDetails.currentLevel;
  }

  /** The monster's on-screen sprite (for positioning scene-level FX like the cave rockfall). */
  get sprite() {
    return this._phaserGameObject;
  }

  /**
   * @param {import('../../types/typedef.js').Monster} monster
   * @returns {void}
   */
  switchMonster(monster) {
    this._monsterDetails = monster;
    this._currentHealth = this._monsterDetails.currentHp;
    this._maxHealth = this._monsterDetails.maxHp;
    this._healthBar.setMeterPercentageAnimated(this._currentHealth / this._maxHealth, {
      skipBattleAnimations: true,
    });
    this.#loadAttacks();
    this._phaserGameObject.setTexture(this._monsterDetails.assetKey, this._monsterDetails.assetFrame || 0);
    this._phaserGameObject.setScale(this._monsterDetails.assetScale || 1);
    this._monsterNameText.setText(this._monsterDetails.name);
    this._setMonsterLevelText();
    this._monsterHealthBarLevelText.setX(this._monsterNameText.width + 35);
  }

  /**
   * @param {number} damage
   * @param {() => void} [callback]
   * @returns {void}
   */
  takeDamage(damage, callback) {
    // update current monster health and animate health bar
    this._currentHealth -= damage;
    if (this._currentHealth < 0) {
      this._currentHealth = 0;
    }
    this._healthBar.setMeterPercentageAnimated(this._currentHealth / this._maxHealth, {
      callback,
      skipBattleAnimations: this._skipBattleAnimations,
    });
  }

  /**
   * @param {() => void} callback
   * @returns {void}
   */
  playMonsterAppearAnimation(callback) {
    throw new Error('playMonsterAppearAnimation is not implemented.');
  }

  /**
   * @param {() => void} callback
   * @returns {void}
   */
  playMonsterHealthBarAppearAnimation(callback) {
    throw new Error('playMonsterHealthBarAppearAnimation is not implemented.');
  }

  /**
   * @param {() => void} callback
   * @returns {void}
   */
  playDeathAnimation(callback) {
    throw new Error('playDeathAnimation is not implemented.');
  }

  /**
   * @param {() => void} callback
   * @returns {void}
   */
  playTakeDamageAnimation(callback) {
    if (this._skipBattleAnimations) {
      this._phaserGameObject.setAlpha(1);
      callback();
      return;
    }

    this._scene.tweens.add({
      delay: 0,
      duration: 150,
      targets: this._phaserGameObject,
      alpha: {
        from: 1,
        start: 1,
        to: 0,
      },
      repeat: 10,
      onComplete: () => {
        this._phaserGameObject.setAlpha(1);
        callback();
      },
    });
  }

  /**
   * @protected
   * @returns {void}
   */
  _setMonsterLevelText() {
    this._monsterHealthBarLevelText.setText(`L${this.level}`);
  }

  /**
   * Truncate a Text game object (adding an ellipsis) so it measures within `maxWidth` using its own
   * font metrics. Keeps the monster name on one line inside the HP panel so neither the name nor the
   * trailing level badge gets clipped at the panel edge.
   * @param {Phaser.GameObjects.Text} textObject
   * @param {number} maxWidth
   * @returns {void}
   */
  #truncateNameToFit(textObject, maxWidth) {
    if (textObject.width <= maxWidth) {
      return;
    }
    const original = textObject.text;
    let s = original;
    while (s.length > 1) {
      s = s.slice(0, -1);
      textObject.setText(`${s}…`);
      if (textObject.width <= maxWidth) {
        return;
      }
    }
  }

  /**
   * Creates the base health bar components for this monster.
   * @param {number} [scaleHealthBarBackgroundImageByY=1] the scaling factor applied to the phaser image game object Y value
   */
  #createHealthBarComponents(scaleHealthBarBackgroundImageByY = 1) {
    this._healthBar = new HealthBar(this._scene, 34, 34);

    this._monsterNameText = this._scene.add.text(30, 20, this.name, {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      color: '#7E3D3F',
      fontSize: '32px',
    });

    const healthBarBgImage = this._scene.add
      .image(0, 0, BATTLE_ASSET_KEYS.HEALTH_BAR_BACKGROUND)
      .setOrigin(0)
      .setScale(1, scaleHealthBarBackgroundImageByY);

    // Keep the name (+ trailing "L<level>" badge) inside the 466px panel: a long monster name used
    // to push the level text past the panel's right edge and clip both. Truncate the name so the
    // name and the level badge always fit; the level badge is then placed just after the name.
    this.#truncateNameToFit(this._monsterNameText, 340);

    this._monsterHealthBarLevelText = this._scene.add.text(this._monsterNameText.width + 35, 23, '', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      color: '#ED474B',
      fontSize: '28px',
    });
    this._setMonsterLevelText();

    const monsterHpText = this._scene.add.text(30, 55, 'HP', {
      fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
      color: '#FF6505',
      fontSize: '24px',
      fontStyle: 'italic',
    });

    this._phaserHealthBarGameContainer = this._scene.add
      .container(0, 0, [
        healthBarBgImage,
        this._monsterNameText,
        this._healthBar.container,
        this._monsterHealthBarLevelText,
        monsterHpText,
      ])
      .setAlpha(0);
  }
}
